import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Button, Icon, Label, Message } from 'semantic-ui-react';
import styled from 'styled-components';
import {
  CritiqueGroup,
  useCritiqueGroupList,
} from 'src/hooks/useCritiqueGroups';
import BetaIcon from 'src/components/BetaIcon';
import FeedbackButton from 'src/components/feedback/FeedbackButton';
import {
  activeMembers,
  FREE_TASTE,
  groupSpineCoverUrl,
  isHost,
  nextCheckpoint,
  userRefId,
  userRefName,
} from 'src/utils/critiqueGroupUtil';
import { MediaQuery } from 'src/constants/size';
import { CritiqueAvatarStack } from 'src/components/critiqueGroups/critiqueAvatar';
import {
  CritiqueEmptyState,
  CritiqueErrorState,
  CritiqueLoadingState,
} from 'src/components/critiqueGroups/critiqueStates';
import { FreeTasteBanner } from 'src/components/critiqueGroups/tierGate';
import { formatCheckpointDate } from 'src/components/critiqueGroups/checkpointTimeline';
import { BookSpine } from 'src/components/critiqueGroups/bookSpine';
import { useCanHostCritiqueGroups } from './useCanHostCritiqueGroups';

/** Design A1 / A2 — where a host lands: empty vs populated, plus the create CTA. */

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const Grid = styled.div`
  margin-top: 26px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
  ${MediaQuery.phone} {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: var(--br-surface);
  border: 1px solid #e7e3d9;
  border-radius: 14px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  /* As a grid item this defaults to min-width: auto, which floors the track at
     the card's min-content — and the book-title line below is nowrap, so that
     floor was ~323px and pushed the page past a 320px phone. Releasing the
     floor is what lets the title's ellipsis do its job instead. */
  min-width: 0;
  box-shadow: 0 6px 18px rgba(45, 40, 25, 0.05);
  cursor: pointer;
  transition: box-shadow 0.15s ease, transform 0.15s ease;
  &:hover {
    box-shadow: 0 10px 24px rgba(45, 40, 25, 0.1);
    transform: translateY(-1px);
  }
`;

const CardFoot = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid #f0ece3;
  padding-top: 12px;
  gap: 8px;
`;

// Small "beta" marker sitting beside the feature title. Uses the critique teal
// so it reads as intentional next to the 26px heading, not like heavy body text.
const BetaTag = styled.span`
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--br-accent);
  background: var(--br-raised-surface);
  border-radius: 999px;
  padding: 2px 9px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const ActivityDot = styled.span<{ $active: boolean }>`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $active }) => ($active ? '#2fa84f' : '#d0cabb')};
  ${({ $active }) => ($active ? 'box-shadow: 0 0 0 4px #e7f4ea;' : '')}
`;

const GroupCard: React.FC<{ group: CritiqueGroup; onOpen: () => void }> = ({
  group,
  onOpen,
}) => {
  const { t } = useTranslation();
  const next = nextCheckpoint(group.checkpoints || []);
  const members = activeMembers(group);
  const firstBook = (group.books || [])[0];
  const bookTitle =
    typeof firstBook === 'string' ? undefined : firstBook?.title;
  // Always the ACTIVE roster, never `memberCount` — that counts every
  // membership row including the host's admin seat and blocked members, so it
  // would read "6 members" on the card where the dashboard reads "5".
  const memberLabel = members.length;

  return (
    <Card
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') onOpen();
      }}
      data-testid={`critique-group-card-${group._id}`}
    >
      <div style={{ display: 'flex', gap: 14 }}>
        <BookSpine
          title={bookTitle}
          coverUrl={groupSpineCoverUrl(group)}
          seed={group._id}
          width={54}
          height={76}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <h4
              style={{
                margin: 0,
                fontSize: 17,
                fontWeight: 800,
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
              }}
            >
              {group.name}
            </h4>
            <ActivityDot $active={!!next} />
          </div>
          <p
            style={{
              margin: '4px 0 0',
              fontSize: 13,
              color: '#8a857b',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {bookTitle || t('CritiqueNoBookLinked', 'No book linked yet')}
          </p>
          <div style={{ marginTop: 10 }}>
            <CritiqueAvatarStack
              people={members.map(m => ({
                id: userRefId(m.user),
                name: userRefName(m.user),
              }))}
            />
          </div>
        </div>
      </div>
      <CardFoot>
        <span style={{ fontSize: 12.5, color: '#6b675e' }}>
          <Icon
            name="flag checkered"
            style={{ color: next ? '#00b5ad' : '#a49f93' }}
          />
          {next
            ? t('CritiqueNext', 'Next: {{label}} — {{date}}', {
                label: next.label,
                date: formatCheckpointDate(next.dueAt),
              })
            : t('CritiqueNoUpcomingCp', 'No upcoming checkpoint')}
        </span>
        <Label color="teal" style={{ borderRadius: 999, fontSize: 11 }}>
          {t('CritiqueNMembers', '{{count}} members', { count: memberLabel })}
        </Label>
      </CardFoot>
    </Card>
  );
};

const CritiqueGroupList: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const userProfile = useSelector((state: any) => state.user.userProfile);
  const { canHost } = useCanHostCritiqueGroups();
  const { data, isLoading, isError, error, refetch } = useCritiqueGroupList();

  const goToUpgrade = () => history.push('/account/subscription');

  if (isLoading) return <CritiqueLoadingState />;
  if (isError) {
    return <CritiqueErrorState message={error?.message} onRetry={() => refetch()} />;
  }

  const groups = data || [];
  const hosted = groups.filter(g => isHost(g, userProfile?._id));
  // A free host has used up the taste once they host one group; the server is
  // still the enforcer — this only decides whether to show the banner.
  const atFreeGroupLimit = !canHost && hosted.length >= FREE_TASTE.maxGroups;

  return (
    <div data-testid="critique-group-list">
      <Header>
        <div>
          <TitleRow>
            <h3 style={{ margin: 0, fontSize: 26, fontWeight: 800 }}>
              {t('CritiqueGroups', 'Critique groups')}
            </h3>
            <BetaTag data-testid="critique-beta-tag">
              {t('BetaLabel', 'Beta')}
            </BetaTag>
            <BetaIcon />
            <FeedbackButton
              featureName={t('CritiqueGroups', 'Critique groups')}
              url={window.location.pathname}
            />
          </TitleRow>
          <p style={{ margin: '4px 0 0', color: '#8a857b', fontSize: 14 }}>
            {groups.length === 0
              ? t('CritiqueListSubtitle', 'Host a circle of readers around your manuscript.')
              : t('CritiqueNActiveGroups', '{{count}} active groups', {
                  count: groups.length,
                })}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Button
            basic
            color="teal"
            onClick={() => history.push('/critique-groups/discover')}
            style={{ borderRadius: 10, fontWeight: 700 }}
            data-testid="discover-groups-button"
          >
            <Icon name="compass" />
            {t('CritiqueDiscover', 'Discover groups')}
          </Button>
          {groups.length > 0 && (
            <Button
              color="teal"
              onClick={() => history.push('/critique-groups/new')}
              style={{ borderRadius: 10, fontWeight: 700 }}
              data-testid="create-group-button"
            >
              <Icon name="plus" />
              {t('CritiqueCreateGroup', 'Create critique group')}
            </Button>
          )}
        </div>
      </Header>

      {/* Open-beta notice: clear "this is early" info + the same feedback entry
          point every other beta feature uses. */}
      <Message
        info
        icon
        size="small"
        style={{ marginTop: 16 }}
        data-testid="critique-beta-banner"
      >
        <Icon name="lab" />
        <Message.Content>
          <Message.Header>
            {t('CritiqueBetaBannerTitle', 'Critique groups is in open beta')}
          </Message.Header>
          {t(
            'CritiqueBetaBannerBody',
            'You’re among the first to try this. Things may still change — tell us what works and what doesn’t.'
          )}{' '}
          <FeedbackButton
            featureName={t('CritiqueGroups', 'Critique groups')}
            url={window.location.pathname}
            trigger={
              <a style={{ cursor: 'pointer', fontWeight: 700 }}>
                {t('CritiqueBetaShareFeedback', 'Share feedback')}
              </a>
            }
          />
        </Message.Content>
      </Message>

      {groups.length === 0 ? (
        <div style={{ marginTop: 30 }}>
          <CritiqueEmptyState
            data-testid="critique-list-empty"
            icon="users"
            title={t('CritiqueEmptyTitle', 'Start your first writing family')}
            body={t(
              'CritiqueEmptyBody',
              'Invite readers to read one of your manuscripts together and give honest, supportive feedback — at a shared pace you set.'
            )}
            action={
              <Button
                color="teal"
                onClick={() => history.push('/critique-groups/new')}
                style={{ borderRadius: 10, fontWeight: 700 }}
                data-testid="create-group-button-empty"
              >
                <Icon name="plus" />
                {t('CritiqueCreateGroup', 'Create critique group')}
              </Button>
            }
            footnote={
              canHost
                ? undefined
                : t(
                    'CritiqueFreePlanLimits',
                    'Free plan · {{groups}} group, up to {{members}} members, {{books}} book',
                    {
                      groups: FREE_TASTE.maxGroups,
                      members: FREE_TASTE.memberCap,
                      books: FREE_TASTE.maxBooks,
                    }
                  )
            }
          />
        </div>
      ) : (
        <Grid>
          {groups.map(g => (
            <GroupCard
              key={g._id}
              group={g}
              onOpen={() => history.push(`/critique-groups/${g._id}`)}
            />
          ))}
        </Grid>
      )}

      {atFreeGroupLimit && <FreeTasteBanner onUpgrade={goToUpgrade} />}
    </div>
  );
};

export default CritiqueGroupList;
